// Scale, tick and path math for the hand-rolled SVG charts. Pure functions
// only, so the tests run under node.

export interface Point {
  x: number;
  y: number;
}

export interface LinearScale {
  (value: number): number;
  invert(px: number): number;
}

export function linearScale(d0: number, d1: number, r0: number, r1: number): LinearScale {
  const span = d1 - d0;
  const scale = ((value: number) => (span === 0 ? (r0 + r1) / 2 : r0 + ((value - d0) / span) * (r1 - r0))) as LinearScale;
  scale.invert = (px: number) => (r1 === r0 ? d0 : d0 + ((px - r0) / (r1 - r0)) * span);
  return scale;
}

export function extent(values: Iterable<number>): [number, number] | null {
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return min <= max ? [min, max] : null;
}

/** Widens [min, max] by a fraction of its span; a flat domain gets a fixed margin. */
export function paddedDomain(min: number, max: number, fraction = 0.05): [number, number] {
  if (min === max) {
    const pad = Math.abs(min) * 0.1 || 1;
    return [min - pad, max + pad];
  }
  const pad = (max - min) * fraction;
  return [min - pad, max + pad];
}

function niceStep(raw: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const n = raw / magnitude;
  const factor = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return factor * magnitude;
}

function roundToStep(value: number, step: number): number {
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
  return Number(value.toFixed(decimals));
}

/** Round tick values (1 / 2 / 2.5 / 5 × 10^k) that bracket [min, max]. */
export function niceTicks(min: number, max: number, count = 5): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min > max) [min, max] = [max, min];
  if (min === max) [min, max] = paddedDomain(min, max);
  const step = niceStep((max - min) / Math.max(1, count - 1));
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= end + step / 2; v += step) ticks.push(roundToStep(v, step));
  return ticks;
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
const TIME_STEPS = [HOUR, 2 * HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR, DAY, 2 * DAY, 3 * DAY, 7 * DAY, 14 * DAY, 30 * DAY];

export interface TimeTicks {
  ticks: number[];
  stepMs: number;
}

/**
 * At most `maxCount` tick times between start and end (ms). Day-or-longer steps
 * land on local midnight; shorter steps count up from the first local midnight.
 */
export function timeTicks(start: number, end: number, maxCount = 5): TimeTicks {
  const span = end - start;
  if (!(span > 0)) return { ticks: [start], stepMs: DAY };
  const stepMs = TIME_STEPS.find((s) => span / s <= maxCount) ?? Math.ceil(span / Math.max(1, maxCount) / DAY) * DAY;
  const ticks: number[] = [];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  if (stepMs < DAY) {
    for (let t = cursor.getTime(); t <= end; t += stepMs) if (t >= start) ticks.push(t);
  } else {
    const days = Math.max(1, Math.round(stepMs / DAY));
    while (cursor.getTime() <= end) {
      if (cursor.getTime() >= start) ticks.push(cursor.getTime());
      cursor.setDate(cursor.getDate() + days);
    }
  }
  return { ticks, stepMs };
}

const monthDay = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const monthDayHour = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric' });
const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });
const plain = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

/** Swift `.dateTime.month().day()` for day steps, `.month().day().hour()` for finer ones. */
export function formatTimeTick(ms: number, stepMs: number): string {
  return (stepMs >= DAY ? monthDay : monthDayHour).format(new Date(ms));
}

/** Axis ticks: full numbers below 10,000, compact (12K) above. */
export function formatAxisValue(value: number): string {
  return Math.abs(value) >= 10_000 ? compact.format(value) : plain.format(value);
}

const f = (v: number) => String(Math.round(v * 100) / 100);

function sign(x: number): number {
  return x < 0 ? -1 : 1;
}

// Fritsch–Carlson slopes as in d3-shape's curveMonotoneX.
function slope3(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): number {
  const h0 = x1 - x0;
  const h1 = x2 - x1;
  const s0 = (y1 - y0) / (h0 || (h1 < 0 ? -0 : 0));
  const s1 = (y2 - y1) / (h1 || (h0 < 0 ? -0 : 0));
  const p = (s0 * h1 + s1 * h0) / (h0 + h1);
  return (sign(s0) + sign(s1)) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p)) || 0;
}

function slope2(x0: number, y0: number, x1: number, y1: number, t: number): number {
  const h = x1 - x0;
  return h ? ((3 * (y1 - y0)) / h - t) / 2 : t;
}

/** Monotone cubic path through x-sorted points: never overshoots the data (Swift Charts `.monotone`). */
export function monotonePath(points: Point[]): string {
  const n = points.length;
  if (n === 0) return '';
  const p0 = points[0];
  if (n === 1) return `M${f(p0.x)},${f(p0.y)}`;
  if (n === 2) return `M${f(p0.x)},${f(p0.y)}L${f(points[1].x)},${f(points[1].y)}`;
  let d = `M${f(p0.x)},${f(p0.y)}`;
  let t0 = 0;
  for (let i = 1; i < n; i++) {
    const a = points[i - 1];
    const b = points[i];
    const t1 = i < n - 1 ? slope3(a.x, a.y, b.x, b.y, points[i + 1].x, points[i + 1].y) : slope2(a.x, a.y, b.x, b.y, t0);
    if (i === 1) t0 = slope2(a.x, a.y, b.x, b.y, t1);
    const dx = (b.x - a.x) / 3;
    d += `C${f(a.x + dx)},${f(a.y + dx * t0)},${f(b.x - dx)},${f(b.y - dx * t1)},${f(b.x)},${f(b.y)}`;
    t0 = t1;
  }
  return d;
}

/** Closes a line path down to the baseline for an area wash. */
export function closeArea(linePath: string, points: Point[], baselineY: number): string {
  if (points.length < 2 || !linePath) return '';
  const first = points[0];
  const last = points[points.length - 1];
  return `${linePath}L${f(last.x)},${f(baselineY)}L${f(first.x)},${f(baselineY)}Z`;
}

/** Index of the value in an ascending array closest to target. */
export function nearestIndex(sorted: number[], target: number): number {
  if (sorted.length === 0) return -1;
  let lo = 0;
  let hi = sorted.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(sorted[lo - 1] - target) <= Math.abs(sorted[lo] - target)) return lo - 1;
  return lo;
}
